/**
 * Compliance Settings Page
 *
 * Configure data retention, privacy, and security policies.
 */
import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from '@tanstack/react-router';
import { Shield, ArrowLeft, Clock, CheckCircle2, MailCheck, AlertTriangle, User, Save, Loader2 } from 'lucide-react';
import { useToast } from '@respectly/ui';
import {
  useSettingsStore,
  useIsGDPREnabled,
  useIsHIPAAEnabled,
} from '../../stores/useSettingsStore';
import { ComplianceSettingsForm } from '../../components/settings';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// ============================================
// Helper Functions
// ============================================

function formatLastUpdated(date: Date | undefined): string {
  if (!date) return 'Never';
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ============================================
// Double Opt-In Settings Card
// ============================================

interface DoiSettings {
  doubleOptInEnabled: boolean;
  doubleOptInAllowTenantOverride: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Sleek iOS-style toggle switch */
function Toggle({
  checked,
  onChange,
  disabled = false,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        width: 40,
        height: 22,
        borderRadius: 11,
        padding: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        border: 'none',
        outline: 'none',
        transition: 'background-color 200ms ease',
        backgroundColor: disabled ? '#d1d5db' : checked ? '#f59e0b' : '#d1d5db',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        style={{
          display: 'block',
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: '#ffffff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
          transition: 'transform 200ms ease',
          transform: checked ? 'translateX(18px)' : 'translateX(0)',
        }}
      />
    </button>
  );
}

function DoubleOptInSettings() {
  const { toast } = useToast();
  const apiBase = import.meta.env.VITE_API_URL ?? '';

  const [settings, setSettings] = useState<DoiSettings | null>(null);
  const [draft, setDraft] = useState<{ doubleOptInEnabled: boolean; doubleOptInAllowTenantOverride: boolean } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/super-admin/compliance`, { credentials: 'include' });
      const json = await res.json();
      if (res.ok && json.success) {
        setSettings(json.data);
        setDraft({
          doubleOptInEnabled: json.data.doubleOptInEnabled,
          doubleOptInAllowTenantOverride: json.data.doubleOptInAllowTenantOverride,
        });
      }
    } catch {
      // fail silently — toast only on save
    } finally {
      setIsLoading(false);
    }
  }, [apiBase]);

  useEffect(() => { void load(); }, [load]);

  const handleToggle = (field: 'doubleOptInEnabled' | 'doubleOptInAllowTenantOverride', value: boolean) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value };
      // If disabling the feature, also disable tenant override
      if (field === 'doubleOptInEnabled' && !value) {
        next.doubleOptInAllowTenantOverride = false;
      }
      return next;
    });
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!draft || isSaving) return;
    setIsSaving(true);
    try {
      const res = await fetch(`${apiBase}/api/super-admin/compliance`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(draft),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error?.message || 'Failed to save');
      setSettings(json.data);
      setIsDirty(false);
      toast({ title: 'Settings saved', description: 'Double opt-in configuration updated.' });
    } catch (err) {
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'An error occurred',
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        overflow: 'hidden',
        marginTop: 24,
      }}>
        {/* Skeleton header */}
        <div style={{ background: '#fffbeb', borderBottom: '1px solid #fde68a', padding: '20px 24px' }}>
          <div style={{ height: 20, width: 220, background: '#fde68a', borderRadius: 6, marginBottom: 8 }} />
          <div style={{ height: 14, width: 340, background: '#fef3c7', borderRadius: 4 }} />
        </div>
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {[1, 2].map((i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: i === 1 ? '1px solid #f3f4f6' : 'none' }}>
              <div>
                <div style={{ height: 14, width: 200, background: '#f3f4f6', borderRadius: 4, marginBottom: 6 }} />
                <div style={{ height: 12, width: 280, background: '#f9fafb', borderRadius: 4 }} />
              </div>
              <div style={{ width: 40, height: 22, background: '#e5e7eb', borderRadius: 11 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!draft) return null;

  const fmtUpdated = settings?.updatedAt
    ? new Date(settings.updatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.15 }}
      style={{
        border: '1px solid #fde68a',
        borderRadius: 12,
        overflow: 'hidden',
        marginTop: 24,
        boxShadow: '0 1px 4px rgba(245,158,11,0.08)',
      }}
    >
      {/* Card header — amber tinted */}
      <div style={{
        background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
        borderBottom: '1px solid #fde68a',
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
      }}>
        <div style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: 'linear-gradient(135deg, #f59e0b, #d97706)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 2px 6px rgba(245,158,11,0.35)',
        }}>
          <MailCheck style={{ width: 18, height: 18, color: '#ffffff' }} />
        </div>
        <div>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#92400e', letterSpacing: '-0.01em' }}>
            Double Opt-In Confirmation
          </h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#a16207', lineHeight: 1.5 }}>
            Require contacts to confirm their opt-in before SMS or email consent becomes active.
            Ensures TCPA compliance and reduces false opt-ins.
          </p>
        </div>
      </div>

      {/* Toggle rows */}
      <div style={{ background: '#ffffff', padding: '0 24px' }}>
        {/* Row 1 — Main feature toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 0',
          borderBottom: '1px solid #f9fafb',
          gap: 16,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 3 }}>
              Require confirmation for Email &amp; SMS opt-ins
            </div>
            <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 }}>
              When enabled, a confirmation message is sent on opt-in. Consent activates only after the contact clicks the link.
            </div>
          </div>
          <Toggle
            checked={draft.doubleOptInEnabled}
            onChange={(v) => handleToggle('doubleOptInEnabled', v)}
          />
        </div>

        {/* Row 2 — Tenant override toggle */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 0',
          gap: 16,
          opacity: draft.doubleOptInEnabled ? 1 : 0.45,
          transition: 'opacity 200ms ease',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                Allow tenants to override this setting
              </span>
              {!draft.doubleOptInEnabled && (
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  fontSize: 10.5,
                  fontWeight: 600,
                  color: '#92400e',
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: 6,
                  padding: '1px 6px',
                }}>
                  <AlertTriangle style={{ width: 9, height: 9 }} />
                  Requires feature enabled
                </span>
              )}
            </div>
            <div style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.5 }}>
              When enabled, each tenant can set their own double opt-in preference from their compliance settings.
            </div>
          </div>
          <Toggle
            checked={draft.doubleOptInAllowTenantOverride}
            onChange={(v) => handleToggle('doubleOptInAllowTenantOverride', v)}
            disabled={!draft.doubleOptInEnabled}
          />
        </div>
      </div>

      {/* Footer — last updated + save */}
      <div style={{
        background: '#fafafa',
        borderTop: '1px solid #f3f4f6',
        padding: '14px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}>
        <div style={{ fontSize: 12, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 6 }}>
          {fmtUpdated ? (
            <>
              <Clock style={{ width: 13, height: 13, flexShrink: 0 }} />
              <span>Updated {fmtUpdated}</span>
              {settings?.updatedBy && (
                <>
                  <span style={{ color: '#e5e7eb' }}>·</span>
                  <User style={{ width: 13, height: 13, flexShrink: 0 }} />
                  <span>{settings.updatedBy}</span>
                </>
              )}
            </>
          ) : (
            <span>Not yet configured</span>
          )}
        </div>

        <AnimatePresence>
          {isDirty && (
            <motion.button
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ duration: 0.15 }}
              onClick={handleSave}
              disabled={isSaving}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 16px',
                borderRadius: 8,
                border: 'none',
                background: isSaving ? '#d97706' : '#f59e0b',
                color: '#ffffff',
                fontSize: 13,
                fontWeight: 600,
                cursor: isSaving ? 'not-allowed' : 'pointer',
                boxShadow: '0 1px 4px rgba(245,158,11,0.4)',
                transition: 'background 150ms ease',
                letterSpacing: '-0.01em',
              }}
            >
              {isSaving
                ? <><Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} />Saving…</>
                : <><Save style={{ width: 14, height: 14 }} />Save Changes</>
              }
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </motion.div>
  );
}

// ============================================
// Component
// ============================================

export function ComplianceSettings() {
  const {
    complianceSettings,
    isLoadingCompliance,
    isSavingCompliance,
    loadComplianceSettings,
    saveComplianceSettings,
  } = useSettingsStore();

  const isGDPREnabled = useIsGDPREnabled();
  const isHIPAAEnabled = useIsHIPAAEnabled();

  // Load settings on mount
  useEffect(() => {
    loadComplianceSettings();
  }, [loadComplianceSettings]);

  // Get active compliance badges
  const complianceBadges = [];
  if (isGDPREnabled) complianceBadges.push('GDPR');
  if (isHIPAAEnabled) complianceBadges.push('HIPAA');
  if (complianceSettings?.ccpaEnabled) complianceBadges.push('CCPA');

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/settings"
              className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/40 dark:to-red-800/40 flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                Compliance Settings
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                Configure data retention, privacy, and security policies
              </p>
            </div>
          </div>

          {/* Compliance badges */}
          {complianceBadges.length > 0 && (
            <div className="flex items-center gap-2">
              {complianceBadges.map((badge) => (
                <div
                  key={badge}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span className="text-xs font-semibold">{badge}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <motion.div variants={itemVariants} className="max-w-2xl">
          <ComplianceSettingsForm
            settings={complianceSettings}
            onSubmit={saveComplianceSettings}
            isLoading={isLoadingCompliance}
            isSaving={isSavingCompliance}
          />

          {/* Last updated */}
          {complianceSettings?.updatedAt && (
            <div className="flex items-center gap-2 mt-6 text-sm text-slate-400 dark:text-slate-500">
              <Clock className="w-4 h-4" />
              <span>Last updated: {formatLastUpdated(complianceSettings.updatedAt)}</span>
            </div>
          )}

          {/* Double Opt-In Settings */}
          <DoubleOptInSettings />
        </motion.div>
      </div>
    </motion.div>
  );
}
